// src/modules/units/tyres/views/retread-batch/retreadbatchlist.view.js
//
// Pure rendering for the retread-batch list — every function takes data and
// returns an HTML string, with no data-fetching or DOM side effects, so it can be
// rendered and asserted in isolation (see retread-batch-views.harness.mjs) and the
// orchestrator (retreadbatchlist.js) stays a thin fetch-and-swap shell.
//
// Layout reuses the register's toolbar + table primitives (tm-toolbar / tm-select
// / tm-table*) and the shared status badge (tm-badge + tone), so a batch row reads
// the same as a tyre row. A batch mid-operation carries a second, toned chip so a
// stuck consignment is visible in the list, not only once you open it.

import { esc, formatShortDate, formatCount } from '../../shared/tyre-format.js';
import { icon } from '../../shared/tyre-icons.js';
import { RETREAD_BATCH_STATUS, OPERATION_STATE } from '../../shared/retread-batch-core.js';
import { batchStatusMeta, operationStateMeta } from '../../shared/retread-batch-vocab.js';

const MUTED = '<span class="tm-muted">—</span>';
const cell = (v) => (v === null || v === undefined || v === '') ? MUTED : esc(v);
const COLSPAN = 5;

// A toned chip for a batch that is mid-operation. IDLE (the settled case) shows
// nothing, so a healthy list is quiet and only exceptions draw the eye.
function operationChip(batch) {
  const state = batch?.operationState;
  if (!state || state === OPERATION_STATE.IDLE) return '';
  const meta = operationStateMeta(state);
  if (!meta.label) return '';
  return ` <span class="tm-badge tm-tone-${meta.tone} tm-rb-opchip">${esc(meta.label)}</span>`;
}

function statusBadge(status) {
  const meta = batchStatusMeta(status);
  return `<span class="tm-badge tm-tone-${meta.tone}">${esc(meta.label)}</span>`;
}

// lineCount with the out/back split as a quiet sub-line, so the one cell answers
// "how big, and how far along" without a second column.
function tyresCell(batch) {
  const total = Number(batch?.lineCount);
  if (!Number.isFinite(total)) return MUTED;
  const out = Number(batch?.outstandingCount);
  const back = Number(batch?.returnedCount);
  const parts = [];
  if (Number.isFinite(out)) parts.push(`${formatCount(out)} out`);
  if (Number.isFinite(back)) parts.push(`${formatCount(back)} back`);
  const sub = parts.length ? `<span class="tm-fact-sub"> · ${parts.join(' · ')}</span>` : '';
  return `${formatCount(total)}${sub}`;
}

export function rowHTML(batch, { supplierName } = {}) {
  const id = esc(batch?.retreadBatchId ?? '');
  return `
    <tr class="tm-row" data-id="${id}">
      <td><b>${cell(batch?.reference)}</b></td>
      <td>${cell(supplierName)}</td>
      <td>${statusBadge(batch?.status)}${operationChip(batch)}</td>
      <td>${tyresCell(batch)}</td>
      <td>${batch?.dispatchedAt ? esc(formatShortDate(batch.dispatchedAt)) : MUTED}</td>
    </tr>`;
}

// Display order for the list: newest batch first. References are sequential and
// unique (RT-YYYY-NNNNN), so ordering by reference DESCENDING puts the latest batch
// on top. Kept here in the pure layer — not as a server `sort` param — so the
// visible order is verified and Node-testable and never rides on an unconfirmed
// sort-direction the backend might silently ignore. Returns a NEW array (never
// mutates its input); a row with no reference sinks to the bottom, since an
// "unknown" reference is never what you want on top.
export function sortBatchesForDisplay(batches) {
  if (!Array.isArray(batches)) return [];
  return batches.slice().sort((a, b) => {
    const ra = a?.reference;
    const rb = b?.reference;
    const na = ra === null || ra === undefined || ra === '';
    const nb = rb === null || rb === undefined || rb === '';
    if (na && nb) return 0;
    if (na) return 1;    // a blank -> after b, regardless of direction
    if (nb) return -1;   // b blank -> after a
    // Descending: compare b against a. numeric-aware so an unpadded "10" beats "2".
    return String(rb).localeCompare(String(ra), undefined, { numeric: true, sensitivity: 'base' });
  });
}

// One state row (loading / empty / error) spanning the table. `retry` adds a retry
// button the orchestrator wires; used for the error case only.
export function stateRowHTML(kind, message, { retry = false } = {}) {
  const ic = kind === 'error' ? 'reports' : 'retread';
  const retryBtn = retry
    ? ` <button type="button" class="tm-btn-link" data-rb-retry>Retry</button>`
    : '';
  return `
    <tr>
      <td class="tm-state-td" colspan="${COLSPAN}">
        <span class="tm-state">
          <span class="tm-state-icon">${icon(ic)}</span>
          <span class="tm-state-msg">${esc(message)}${retryBtn}</span>
        </span>
      </td>
    </tr>`;
}

// Status filter options — All plus every batch status, in lifecycle order.
const STATUS_FILTER_ORDER = [
  RETREAD_BATCH_STATUS.DRAFT,
  RETREAD_BATCH_STATUS.OPEN,
  RETREAD_BATCH_STATUS.PARTIALLY_RETURNED,
  RETREAD_BATCH_STATUS.CLOSED,
  RETREAD_BATCH_STATUS.CANCELLED
];

function statusOptions(selected) {
  const opts = [`<option value="">All statuses</option>`];
  for (const s of STATUS_FILTER_ORDER) {
    const sel = s === selected ? ' selected' : '';
    opts.push(`<option value="${esc(s)}"${sel}>${esc(batchStatusMeta(s).label)}</option>`);
  }
  return opts.join('');
}

function supplierOptions(suppliers, selected) {
  const opts = [`<option value="">All suppliers</option>`];
  for (const s of suppliers || []) {
    const id = s?.supplierId;
    if (id === undefined || id === null) continue;
    const sel = String(id) === String(selected) ? ' selected' : '';
    opts.push(`<option value="${esc(id)}"${sel}>${esc(s.supplierName || 'Unnamed supplier')}</option>`);
  }
  return opts.join('');
}

// The whole screen shell. `tbodyInner` is a state row on first paint; the
// orchestrator swaps in real rows once the fetch resolves. `canManage` shows the
// New button only to users who may create a batch (administrators).
export function listHTML({ status = '', supplierId = '', suppliers = [], tbodyInner = '', canManage = false } = {}) {
  const newBtn = canManage
    ? `<div class="tm-toolbar-trailing"><button type="button" class="tm-btn-primary" data-rb-new>New batch</button></div>`
    : '';
  return `
    <div class="tm-register tm-rb-list">
      <div class="tm-toolbar">
        <select class="tm-select" data-rb-filter="status" aria-label="Filter by status">
          ${statusOptions(status)}
        </select>
        <select class="tm-select" data-rb-filter="supplier" aria-label="Filter by supplier">
          ${supplierOptions(suppliers, supplierId)}
        </select>
        ${newBtn}
      </div>

      <div class="tm-table-wrap">
        <table class="tm-table">
          <thead>
            <tr>
              <th>Reference</th>
              <th>Supplier</th>
              <th>Status</th>
              <th>Tyres</th>
              <th>Dispatched</th>
            </tr>
          </thead>
          <tbody data-rb-rows>${tbodyInner}</tbody>
        </table>
      </div>
    </div>`;
}

export const LIST_COLSPAN = COLSPAN;
